import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getServices,
  getVirtualCategories,
  categoryNameToSlug,
  Service,
  VirtualCategory,
} from "../api";

type FeaturedTile = {
  id: string;
  name: string;
  icon: string;
  slug: string;
  isVirtual: boolean;
};

export default function BrowseServicesPage() {
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [virtuals, setVirtuals] = useState<VirtualCategory[]>([]);
  const [zip, setZip] = useState<string>(localStorage.getItem("mouna_zip") || "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([getServices(), getVirtualCategories()])
      .then(([svc, vc]) => {
        setServices(svc);
        setVirtuals(vc);
      })
      .catch((err) => {
        console.error("Failed to load services", err);
        setError("Couldn't load services. Please try again.");
      })
      .finally(() => setLoading(false));
  }, []);

  function saveZip(z: string) {
    setZip(z);
    if (z) localStorage.setItem("mouna_zip", z);
    else localStorage.removeItem("mouna_zip");
  }

  // Build the Popular grid: 6 featured real categories + 2 virtual categories,
  // ordered by displayOrder
  const featuredTiles: FeaturedTile[] = [
    ...services
      .filter((s) => s.featured)
      .map((s) => ({
        id: s.categoryId,
        name: s.name,
        icon: s.icon || "📋",
        slug: categoryNameToSlug(s.name),
        isVirtual: false,
        displayOrder: s.displayOrder || 99,
      })),
    ...virtuals.map((v) => ({
      id: v.virtualCategoryId,
      name: v.name,
      icon: v.icon || "📋",
      slug: v.virtualCategoryId,
      isVirtual: true,
      displayOrder: v.displayOrder || 99,
    })),
  ]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map(({ displayOrder, ...rest }) => rest);

  // All real categories for the bottom grid (alphabetical)
  const allCategories = [...services].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="font-display text-lg font-bold text-navy"
          >
            Mouna
          </button>
          <button
            onClick={() => navigate("/login")}
            className="text-sm font-bold text-mouna-blue"
          >
            Sign in
          </button>
        </div>
      </header>

      {/* Hero with ZIP picker */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-navy mb-2">
            What do you need help with?
          </h1>
          <p className="text-sm text-gray-600 mb-4">
            Browse 36+ home services from trusted pros
          </p>
          <div className="flex gap-2 max-w-md">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                📍
              </span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={5}
                value={zip}
                onChange={(e) => saveZip(e.target.value.replace(/\D/g, ""))}
                placeholder="Enter ZIP code"
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-300 focus:border-mouna-blue focus:outline-none text-sm"
              />
            </div>
            {zip && (
              <button
                onClick={() => saveZip("")}
                className="px-3 py-2.5 text-xs text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {loading && (
        <div className="max-w-6xl mx-auto px-4 py-16 text-center text-gray-400 text-sm">
          Loading services…
        </div>
      )}
      {error && (
        <div className="max-w-6xl mx-auto px-4 py-8 text-center text-red-600 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Popular services grid */}
          <section className="max-w-6xl mx-auto px-4 pt-8 pb-4">
            <h2 className="font-display text-xl font-bold text-navy mb-4">
              ⭐ Popular Services
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {featuredTiles.map((tile) => (
                <button
                  key={tile.id}
                  onClick={() => navigate(`/browse/${tile.slug}`)}
                  className="group flex flex-col items-center gap-2 p-3 rounded-2xl bg-white hover:shadow-lg transition-all border border-gray-100"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center text-2xl shadow-sm group-hover:scale-110 transition-transform">
                    {tile.icon}
                  </div>
                  <span className="text-xs font-bold text-navy text-center leading-tight">
                    {tile.name}
                  </span>
                </button>
              ))}
            </div>
          </section>

          {/* All categories grid */}
          <section className="max-w-6xl mx-auto px-4 pt-8">
            <h2 className="font-display text-xl font-bold text-navy mb-4">
              All Categories ({allCategories.length})
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {allCategories.map((cat) => (
                <button
                  key={cat.categoryId}
                  onClick={() =>
                    navigate(`/browse/${categoryNameToSlug(cat.name)}`)
                  }
                  className="flex items-center gap-3 p-4 rounded-xl bg-white hover:shadow-md transition-all border border-gray-100 text-left"
                >
                  <span className="text-2xl">{cat.icon || "📋"}</span>
                  <div className="min-w-0">
                    <div className="font-bold text-navy text-sm leading-tight truncate">
                      {cat.name}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {cat.subcategories?.length || 0} services
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
